import React from 'react';
import { CustomModal } from '../../../../common/CustomModal';
import {
  CheckCircle,
  XCircle,
  X,
  ArrowLeft,
  FileSpreadsheet,
  Upload,
  FileText,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'react-toastify';
import '../../../../../styles/components/action-modal.scss';
import '../../../../../styles/components/withdrawal-action-modal.scss';
import { StatusNewBadge } from '../../../../common/StatusBadge';
import { NEW_TRANSACTION_STATUS, TRANSACTION_TYPES } from '../../../../../constants/status';
import i18n from '../../../../../i18n';
import { withdrawalAction } from '../services/transactionService';
import { imageUrl } from '../../../../../constants/global';

const ROLE_ADMIN = 'A';
const ROLE_FINANCE_MANAGER = 'F';

const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

// Order matters — this defines which field receives focus first when
// multiple validation errors exist.
const ADMIN_FIELD_ORDER = ['bank', 'beneficiary', 'account_number', 'project_name', 'file_receipt'];
const FINANCE_FIELD_ORDER = ['file_fm'];

const PREVIEW_URL_KEY = {
  file_receipt: 'receiptPreviewUrl',
  file_fm: 'fmPreviewUrl',
};

const INPUT_ID = {
  file_receipt: 'wam-file-receipt',
  file_fm: 'wam-file-fm',
};

export class WithdrawalActionModal extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      submitting: false,
      rejectReason: '',
      showRejectInput: false,
      rejectError: '',

      bank: props.selectedTransaction?.bank || '',
      beneficiary: props.selectedTransaction?.beneficiary || '',
      account_number: props.selectedTransaction?.account_number || '',
      project_name: props.selectedTransaction?.remarks || '',

      file_receipt: null,
      file_fm: null,
      receiptPreviewUrl: null,
      fmPreviewUrl: null,
      dragOver: { file_receipt: false, file_fm: false },

      errors: {},
    };

    this.bankRef = React.createRef();
    this.beneficiaryRef = React.createRef();
    this.accountNumberRef = React.createRef();
    this.projectNameRef = React.createRef();
    this.fileReceiptRef = React.createRef();
    this.fileFmRef = React.createRef();
    this.rejectReasonRef = React.createRef();

    this.fieldRefMap = {
      bank: this.bankRef,
      beneficiary: this.beneficiaryRef,
      account_number: this.accountNumberRef,
      project_name: this.projectNameRef,
      file_receipt: this.fileReceiptRef,
      file_fm: this.fileFmRef,
    };
  }

  componentDidUpdate(prevProps, prevState) {
    const { show, selectedTransaction } = this.props;

    // Re-seed the form whenever the modal is (re)opened for a transaction,
    // or when the selected transaction itself changes while open.
    const justOpened = show && !prevProps.show;
    const transactionChanged = selectedTransaction?.id !== prevProps.selectedTransaction?.id;

    if (justOpened || (show && transactionChanged)) {
      this.initializeFromTransaction();
    }

    // Move focus into the reject textarea the moment it appears.
    if (this.state.showRejectInput && !prevState.showRejectInput) {
      this.rejectReasonRef.current?.focus();
    }
  }

  componentWillUnmount() {
    this.revokePreviewUrl('receiptPreviewUrl');
    this.revokePreviewUrl('fmPreviewUrl');
  }

  revokePreviewUrl = (previewUrlKey) => {
    if (this.state[previewUrlKey]) {
      URL.revokeObjectURL(this.state[previewUrlKey]);
    }
  };

  initializeFromTransaction = () => {
    const { selectedTransaction } = this.props;

    this.revokePreviewUrl('receiptPreviewUrl');
    this.revokePreviewUrl('fmPreviewUrl');

    this.setState({
      bank: selectedTransaction?.bank || '',
      beneficiary: selectedTransaction?.beneficiary || '',
      account_number: selectedTransaction?.account_number || '',
      project_name: selectedTransaction?.remarks || '',
      file_receipt: null,
      file_fm: null,
      receiptPreviewUrl: null,
      fmPreviewUrl: null,
      dragOver: { file_receipt: false, file_fm: false },
      errors: {},
      rejectReason: '',
      showRejectInput: false,
      rejectError: '',
      submitting: false,
    });
  };

  handleFieldChange = (field) => (e) => {
    const { value } = e.target;
    this.setState((prevState) => ({
      [field]: value,
      errors: { ...prevState.errors, [field]: '' },
    }));
  };

  formatFileSize = (bytes) => {
    if (!bytes) return '0 KB';
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(2)} MB`;
  };

  validateFile = (file) => {
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      return i18n.t('invalid_file_type') || 'Only JPG, PNG or PDF files are allowed';
    }
    if (file.size > MAX_FILE_SIZE) {
      return i18n.t('file_too_large') || 'File size must not exceed 5 MB';
    }
    return '';
  };

  setFileForField = (field, file) => {
    if (!file) return;

    const error = this.validateFile(file);
    const previewUrlKey = PREVIEW_URL_KEY[field];

    if (error) {
      this.setState((prevState) => ({
        errors: { ...prevState.errors, [field]: error },
      }));
      return;
    }

    this.revokePreviewUrl(previewUrlKey);
    const isImageFile = file.type.startsWith('image/');
    const newPreviewUrl = isImageFile ? URL.createObjectURL(file) : null;

    this.setState((prevState) => ({
      [field]: file,
      [previewUrlKey]: newPreviewUrl,
      errors: { ...prevState.errors, [field]: '' },
    }));
  };

  handleFileInputChange = (field) => (e) => {
    const file = e.target.files?.[0] || null;
    this.setFileForField(field, file);
    // allow re-selecting the same file later
    e.target.value = '';
  };

  handleDragOver = (field) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (this.state.submitting) return;
    this.setState((prevState) => ({ dragOver: { ...prevState.dragOver, [field]: true } }));
  };

  handleDragLeave = (field) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState((prevState) => ({ dragOver: { ...prevState.dragOver, [field]: false } }));
  };

  handleDrop = (field) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState((prevState) => ({ dragOver: { ...prevState.dragOver, [field]: false } }));
    if (this.state.submitting) return;
    const file = e.dataTransfer.files?.[0] || null;
    this.setFileForField(field, file);
  };

  removeFile = (field) => () => {
    const previewUrlKey = PREVIEW_URL_KEY[field];
    this.revokePreviewUrl(previewUrlKey);
    this.setState({ [field]: null, [previewUrlKey]: null });
  };

  validateApprove = () => {
    const { role } = this.props;
    const { bank, beneficiary, account_number, project_name, file_receipt, file_fm } = this.state;
    const errors = {};

    if (role === ROLE_ADMIN) {
      if (!bank.trim()) errors.bank = i18n.t('bank_is_required') || 'Bank is required';
      if (!beneficiary.trim())
        errors.beneficiary = i18n.t('beneficiary_is_required') || 'Beneficiary is required';
      if (!account_number.trim())
        errors.account_number =
          i18n.t('account_number_is_required') || 'Account number is required';
      if (!project_name.trim())
        errors.project_name = i18n.t('project_name_is_required') || 'Project name is required';
      if (!file_receipt)
        errors.file_receipt = i18n.t('receipt_is_required') || 'Receipt file is required';
    }

    if (role === ROLE_FINANCE_MANAGER) {
      if (!file_fm)
        errors.file_fm = i18n.t('fm_attachment_is_required') || 'FM attachment is required';
    }

    return errors;
  };

  focusFirstError = (errors) => {
    const { role } = this.props;
    const order = role === ROLE_ADMIN ? ADMIN_FIELD_ORDER : FINANCE_FIELD_ORDER;
    const firstKey = order.find((key) => errors[key]);
    if (firstKey) {
      this.fieldRefMap[firstKey]?.current?.focus();
      this.fieldRefMap[firstKey]?.current?.scrollIntoView?.({
        behavior: 'smooth',
        block: 'center',
      });
    }
  };

  handleApprove = async () => {
    const { selectedTransaction, onComplete, role } = this.props;

    const errors = this.validateApprove();
    if (Object.keys(errors).length > 0) {
      this.setState({ errors });
      this.focusFirstError(errors);
      return;
    }

    try {
      this.setState({ submitting: true, errors: {} });
      const payload = new FormData();

      payload.append('id', selectedTransaction.id);
      payload.append('action', 'approve');

      if (role === ROLE_ADMIN) {
        payload.append('bank', this.state.bank);
        payload.append('beneficiary', this.state.beneficiary);
        payload.append('account_number', this.state.account_number);
        payload.append('project_name', this.state.project_name);
        payload.append('file_receipt', this.state.file_receipt);
      }

      if (role === ROLE_FINANCE_MANAGER) {
        payload.append('file_fm', this.state.file_fm);
      }

      const response = await withdrawalAction(payload);

      if (response && response.success) {
        toast.success(
          response.message ||
            i18n.t('payment_approved_successfully') ||
            'Payment approved successfully'
        );
        onComplete?.();
        this.handleClose();
      } else {
        toast.error(response?.message || i18n.t('something_went_wrong'));
      }
    } catch (error) {
      toast.error(i18n.t('something_went_wrong'));
    } finally {
      this.setState({ submitting: false });
    }
  };

  handleReject = async () => {
    const { selectedTransaction, onComplete } = this.props;
    const { rejectReason } = this.state;

    if (!rejectReason.trim()) {
      this.setState({
        rejectError: i18n.t('reject_reason_is_required') || 'Reject reason is required',
      });
      this.rejectReasonRef.current?.focus();
      return;
    }

    try {
      this.setState({ submitting: true, rejectError: '' });
      const payload = {
        id: selectedTransaction.id,
        action: 'reject',
        reason: rejectReason.trim(),
      };

      const response = await withdrawalAction(payload);

      if (response.success) {
        toast.success(
          response.message ||
            i18n.t('payment_rejected_successfully') ||
            'Payment rejected successfully'
        );
        onComplete?.();
        this.handleClose();
      } else {
        toast.error(
          response.message || i18n.t('failed_to_reject_payment') || 'Failed to reject payment'
        );
      }
    } catch (error) {
      console.error(error);
      toast.error(i18n.t('something_went_wrong'));
    } finally {
      this.setState({ submitting: false });
    }
  };

  handleRejectClick = () => {
    this.setState({ showRejectInput: true });
  };

  handleBackFromReject = () => {
    this.setState({ showRejectInput: false, rejectReason: '', rejectError: '' });
  };

  handleClose = () => {
    this.revokePreviewUrl('receiptPreviewUrl');
    this.revokePreviewUrl('fmPreviewUrl');
    this.setState({
      rejectReason: '',
      showRejectInput: false,
      rejectError: '',
      submitting: false,
      errors: {},
    });
    this.props.onClose();
  };

  // Shared drag-and-drop upload zone + preview card, used for both the
  // Admin "Upload Receipt" field and the Finance Manager "FM Attachment" field.
  renderFileUploadField = (field, label) => {
    const { submitting, errors, dragOver } = this.state;
    const file = this.state[field];
    const previewUrl = this.state[PREVIEW_URL_KEY[field]];
    const isDraggingOver = dragOver[field];
    const isImage = file && file.type?.startsWith('image/');
    const isPdf = file && file.type === 'application/pdf';
    const inputId = INPUT_ID[field];
    const fieldRef = this.fieldRefMap[field];

    return (
      <div className="wam-form-group wam-form-group--file">
        <label className="wam-form-label" htmlFor={inputId}>
          {label} <span className="wam-required-mark">*</span>
        </label>

        <input
          type="file"
          id={inputId}
          name={field}
          accept=".jpg,.jpeg,.png,.pdf"
          style={{ display: 'none' }}
          disabled={submitting}
          onChange={this.handleFileInputChange(field)}
        />

        {!file ? (
          <div
            className={`wam-upload-zone ${isDraggingOver ? 'wam-upload-zone--dragover' : ''} ${
              submitting ? 'wam-upload-zone--disabled' : ''
            } ${errors[field] ? 'wam-upload-zone--error' : ''}`}
            onDragOver={this.handleDragOver(field)}
            onDragLeave={this.handleDragLeave(field)}
            onDrop={this.handleDrop(field)}
          >
            <div className="wam-upload-zone__icon">
              <Upload size={24} />
            </div>
            <p className="wam-upload-zone__title">{i18n.t('drop_file_here') || 'Drop file here'}</p>
            <p className="wam-upload-zone__sub">JPG, PNG or PDF — max 5 MB</p>
            <button
              type="button"
              ref={fieldRef}
              className="wam-upload-zone__btn"
              disabled={submitting}
              onClick={() => document.getElementById(inputId).click()}
            >
              {i18n.t('browse_file') || 'Browse File'}
            </button>
          </div>
        ) : (
          <div className="wam-receipt-preview">
            <div className="wam-receipt-preview__header">
              <FileText size={15} className="wam-receipt-preview__file-icon" />
              <span className="wam-receipt-preview__name">{file.name}</span>
              <span className="wam-receipt-preview__size">{this.formatFileSize(file.size)}</span>
              <button
                type="button"
                className="wam-receipt-preview__remove"
                onClick={this.removeFile(field)}
                disabled={submitting}
              >
                <X size={12} /> {i18n.t('remove') || 'Remove'}
              </button>
            </div>

            <div
              className={`wam-receipt-preview__body ${
                isImage ? 'wam-receipt-preview__body--image' : ''
              }`}
            >
              {isImage && previewUrl && (
                <img src={previewUrl} alt="File preview" className="wam-receipt-preview__img" />
              )}
              {isPdf && (
                <div className="wam-receipt-preview__pdf">
                  <FileText size={32} className="wam-receipt-preview__pdf-icon" />
                  <div>
                    <p className="wam-receipt-preview__pdf-name">{file.name}</p>
                    <span className="wam-receipt-preview__pdf-meta">
                      PDF document · {this.formatFileSize(file.size)}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="wam-receipt-preview__actions">
              <button
                type="button"
                className="wam-receipt-preview__change-btn"
                onClick={() => document.getElementById(inputId).click()}
                disabled={submitting}
              >
                <Upload size={13} /> {i18n.t('change_file') || 'Change File'}
              </button>
            </div>
          </div>
        )}

        {errors[field] && (
          <div id={`${inputId}-error`} className="wam-upload-error">
            <AlertCircle size={13} />
            {errors[field]}
          </div>
        )}
      </div>
    );
  };

  renderAdminFields() {
    const { submitting, bank, beneficiary, account_number, project_name, errors } = this.state;

    return (
      <div className="wam-form-grid">
        <div className="wam-form-group">
          <label className="wam-form-label" htmlFor="wam-bank">
            {i18n.t('bank')} <span className="wam-required-mark">*</span>
          </label>
          <input
            id="wam-bank"
            ref={this.bankRef}
            type="text"
            className={`wam-form-input ${errors.bank ? 'wam-form-input--error' : ''}`}
            value={bank}
            onChange={this.handleFieldChange('bank')}
            placeholder="Enter bank name"
            disabled={submitting}
            aria-invalid={!!errors.bank}
            aria-describedby={errors.bank ? 'wam-bank-error' : undefined}
          />
          {errors.bank && (
            <span id="wam-bank-error" className="wam-field-error">
              {errors.bank}
            </span>
          )}
        </div>

        <div className="wam-form-group">
          <label className="wam-form-label" htmlFor="wam-beneficiary">
            {i18n.t('beneficiary') || 'Beneficiary'} <span className="wam-required-mark">*</span>
          </label>
          <input
            id="wam-beneficiary"
            ref={this.beneficiaryRef}
            type="text"
            className={`wam-form-input ${errors.beneficiary ? 'wam-form-input--error' : ''}`}
            value={beneficiary}
            onChange={this.handleFieldChange('beneficiary')}
            placeholder="Enter beneficiary name"
            disabled={submitting}
            aria-invalid={!!errors.beneficiary}
            aria-describedby={errors.beneficiary ? 'wam-beneficiary-error' : undefined}
          />
          {errors.beneficiary && (
            <span id="wam-beneficiary-error" className="wam-field-error">
              {errors.beneficiary}
            </span>
          )}
        </div>

        <div className="wam-form-group">
          <label className="wam-form-label" htmlFor="wam-account-number">
            {i18n.t('ac_no')} <span className="wam-required-mark">*</span>
          </label>
          <input
            id="wam-account-number"
            ref={this.accountNumberRef}
            type="text"
            className={`wam-form-input ${errors.account_number ? 'wam-form-input--error' : ''}`}
            value={account_number}
            onChange={this.handleFieldChange('account_number')}
            placeholder="Enter account number"
            disabled={submitting}
            aria-invalid={!!errors.account_number}
            aria-describedby={errors.account_number ? 'wam-account-number-error' : undefined}
          />
          {errors.account_number && (
            <span id="wam-account-number-error" className="wam-field-error">
              {errors.account_number}
            </span>
          )}
        </div>

        <div className="wam-form-group">
          <label className="wam-form-label" htmlFor="wam-project-name">
            Project Name <span className="wam-required-mark">*</span>
          </label>
          <input
            id="wam-project-name"
            ref={this.projectNameRef}
            type="text"
            className={`wam-form-input ${errors.project_name ? 'wam-form-input--error' : ''}`}
            value={project_name}
            onChange={this.handleFieldChange('project_name')}
            placeholder="Enter project name"
            disabled={submitting}
            aria-invalid={!!errors.project_name}
            aria-describedby={errors.project_name ? 'wam-project-name-error' : undefined}
          />
          {errors.project_name && (
            <span id="wam-project-name-error" className="wam-field-error">
              {errors.project_name}
            </span>
          )}
        </div>

        {this.renderFileUploadField('file_receipt', 'Upload Receipt')}
      </div>
    );
  }

  renderFinanceFields() {
    const { bank, beneficiary, account_number, project_name } = this.state;

    return (
      <div className="wam-form-grid">
        <div className="wam-form-group">
          <span className="wam-form-label">{i18n.t('bank')}</span>
          <div className="wam-readonly-value">{bank || '--'}</div>
        </div>

        <div className="wam-form-group">
          <span className="wam-form-label">{i18n.t('beneficiary') || 'Beneficiary'}</span>
          <div className="wam-readonly-value">{beneficiary || '--'}</div>
        </div>

        <div className="wam-form-group">
          <span className="wam-form-label">{i18n.t('ac_no')}</span>
          <div className="wam-readonly-value">{account_number || '--'}</div>
        </div>

        <div className="wam-form-group">
          <span className="wam-form-label">Project Name</span>
          <div className="wam-readonly-value">{project_name || '--'}</div>
        </div>

        {this.renderFileUploadField('file_fm', 'FM Attachment')}
      </div>
    );
  }

  render() {
    const { show, selectedTransaction, role } = this.props;
    const { rejectReason, submitting, showRejectInput, rejectError } = this.state;

    if (!selectedTransaction) return null;

    const isAdmin = role === ROLE_ADMIN;

    return (
      <CustomModal isOpen={show} header="Withdrawal Action Required" onClose={this.handleClose}>
        <div className="action-modal-container">
          {/* Payment Details Section */}
          <div className="payment-details-card">
            <h6>Transaction Details</h6>
            <div className="details-grid">
              <div className="detail-item-new">
                <span className="detail-label">{i18n.t('bidder_id')}</span>
                <span className="detail-value success">{selectedTransaction.user || '--'}</span>
              </div>
              <div className="detail-item-new">
                <span className="detail-label">{i18n.t('name')}</span>
                <span className="detail-value ">
                  {selectedTransaction?.user_relation?.name || '--'}
                </span>
              </div>

              <div className="detail-item-new">
                <span className="detail-label">
                  {selectedTransaction.type === 'wallet_recharge'
                    ? i18n.t('credit')
                    : i18n.t('debit')}
                </span>
                <span className="detail-value danger">
                  {selectedTransaction.type === 'wallet_recharge'
                    ? selectedTransaction.credit || '-'
                    : selectedTransaction.debit || '-'}
                </span>
              </div>
              <div className="detail-item-new">
                <span className="detail-label">Balance</span>
                <span className="detail-value danger">{selectedTransaction.balance || '-'}</span>
              </div>

              <div className="detail-item-new">
                <span className="detail-label">{i18n.t('bank')}</span>
                <span className="detail-value ">{selectedTransaction.bank || '--'}</span>
              </div>
              <div className="detail-item-new">
                <span className="detail-label">{i18n.t('beneficiary')}</span>
                <span className="detail-value ">{selectedTransaction.beneficiary || '-'}</span>
              </div>
              <div className="detail-item-new">
                <span className="detail-label">{i18n.t('ac_no')}</span>
                <span className="detail-value ">{selectedTransaction.account_number || '-'}</span>
              </div>

              <div className="detail-item-new">
                <span className="detail-label">Receipt</span>
                <span className={`detail-value  `}>
                  {selectedTransaction.file_receipt ? (
                    <a
                      className="button-table"
                      href={`${imageUrl}uploads/wallet/receipt/${selectedTransaction.file_receipt}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FileSpreadsheet size={18} /> {i18n.t('receipt')}
                    </a>
                  ) : (
                    <span className="attachment-empty">No Receipt</span>
                  )}
                </span>
              </div>
              <div className="detail-item-new">
                <span className="detail-label">Status</span>
                <span className={`detail-value  `}>
                  <StatusNewBadge status={NEW_TRANSACTION_STATUS[selectedTransaction.status]} />
                </span>
              </div>
              <div className="detail-item-new">
                <span className="detail-label">{i18n.t('type')}</span>
                <span className="detail-value amount">
                  {TRANSACTION_TYPES[selectedTransaction.type]?.text ||
                    selectedTransaction.type ||
                    '-'}
                </span>
              </div>
              <div className="detail-item-new">
                <span className="detail-label">{i18n.t('method')}</span>
                <span className="detail-value ">{selectedTransaction.method || '-'}</span>
              </div>
            </div>
          </div>

          {/* Role-driven Action Form */}
          {!showRejectInput && (
            <div className="wam-form-section">
              <h6 className="wam-form-title">
                {isAdmin ? 'Admin Action' : 'Finance Manager Action'}
              </h6>
              {isAdmin ? this.renderAdminFields() : this.renderFinanceFields()}
            </div>
          )}

          {/* Reject Reason Section - Shows when reject is clicked */}
          {showRejectInput && (
            <div className="reject-section">
              <label htmlFor="rejectReason" className="reject-label">
                Reason for Rejection <span className="required">*</span>
              </label>
              <textarea
                id="rejectReason"
                ref={this.rejectReasonRef}
                className={`reject-textarea ${rejectError ? 'error' : ''}`}
                value={rejectReason}
                onChange={(e) =>
                  this.setState({
                    rejectReason: e.target.value,
                    rejectError: '',
                  })
                }
                placeholder="Enter the reason for rejecting this payment..."
                rows={4}
                disabled={submitting}
                aria-invalid={!!rejectError}
                aria-describedby={rejectError ? 'wam-reject-reason-error' : undefined}
              />
              {rejectError && (
                <span id="wam-reject-reason-error" className="error-message">
                  {rejectError}
                </span>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="action-buttons-container">
            {!showRejectInput ? (
              <>
                <button
                  className="modal-btn btn-cancel"
                  onClick={this.handleClose}
                  disabled={submitting}
                >
                  Cancel
                </button>

                <button
                  className="modal-btn btn-danger"
                  onClick={this.handleRejectClick}
                  disabled={submitting}
                >
                  <X size={18} />
                  Reject
                </button>

                <button
                  className="modal-btn btn-success"
                  onClick={this.handleApprove}
                  disabled={submitting}
                >
                  <CheckCircle size={18} />
                  {submitting ? 'Approving...' : 'Approve'}
                </button>
              </>
            ) : (
              <>
                <button
                  className="modal-btn btn-cancel"
                  onClick={this.handleBackFromReject}
                  disabled={submitting}
                >
                  <ArrowLeft size={18} />
                  Back
                </button>

                <button
                  className="modal-btn btn-confirm"
                  onClick={this.handleReject}
                  disabled={submitting || !rejectReason.trim()}
                >
                  <XCircle size={18} />
                  {submitting ? 'Rejecting...' : 'Confirm Reject'}
                </button>
              </>
            )}
          </div>
        </div>
      </CustomModal>
    );
  }
}
